import React from "react";
import { Helmet } from "react-helmet-async";
import {
  trackButtonClick,
  trackVideoView,
  trackContactClick,
} from "./utils/analytics";
import { handleAffiliateLink } from "./utils/affiliateUtils";
import LanguageSelector from "./components/LanguageSelector"; // Thêm import LanguageSelector
import { trackEvent } from "./utils/metaPixel";
import { captureFacebookAdParams } from "./utils/adTracking";
import { captureFirstTouch } from "./utils/sourceTracking";
import { sendToTelegram } from "./utils/telegramNotification";
import { Snowfall, SnowToggleButton, useSnowEffect } from "./components/Snowfall";
import AffiliateBanner from "./components/AffiliateBanner";

// Simple icon components
const IconBrain = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-10 w-10 text-white"
  >
    <circle cx="12" cy="12" r="8" />
    <path d="M10 8 L14 12 L10 16" />
  </svg>
);

const IconClock = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-10 w-10 text-white"
  >
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8 L12 12 L15 14" />
  </svg>
);

const IconChart = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-10 w-10 text-white"
  >
    <path d="M4 14h4v6H4zM10 10h4v10h-4zM16 6h4v14h-4z" />
  </svg>
);

const IconChat = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-10 w-10 text-white"
  >
    <path d="M4 5h16v10H8l-4 4V5z" />
    <path d="M8 9h8M8 12h5" strokeLinecap="round" />
  </svg>
);

// Mảng dữ liệu cho tính năng nổi bật
const features = [
  {
    icon: <IconBrain />,
    title: "AI Viết Content Đa Kênh",
    description:
      "Tự động tạo content bán hàng cho Facebook Fanpage, bài SEO website WordPress và bài Zalo OA theo từng ngữ cảnh kênh.",
  },
  {
    icon: <IconClock />,
    title: "Tự Động Đăng Bài Đa Nền Tảng",
    description:
      "Lên lịch và đăng bài tự động lên Facebook, WordPress/WooCommerce và Zalo OA trong một hệ thống quản lý tập trung.",
  },
  {
    icon: <IconChart />,
    title: "WordPress SEO & WooCommerce",
    description:
      "AI viết bài chuẩn SEO WordPress, tạo meta description, focus keyword, tags và chèn link sản phẩm WooCommerce.",
  },
  {
    icon: <IconChat />,
    title: "Auto Comment & Lead Fanpage",
    description:
      "Tự động trả lời bình luận Facebook, nhận diện lead nóng và hỗ trợ ẩn comment nhạy cảm để giảm thất thoát khách hàng.",
  },
];

const useScrollToSection = () => {
  const scrollToSection = React.useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }, []);

  return scrollToSection;
};

const useScrollAnimation = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const domRef = React.useRef();

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setIsVisible(entry.isIntersecting));
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    const { current } = domRef;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return [domRef, isVisible];
};

const useScrollAnimationOnce = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [hasAnimated, setHasAnimated] = React.useState(false);
  const domRef = React.useRef();

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setIsVisible(true);
            setHasAnimated(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    const { current } = domRef;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [hasAnimated]);

  return [domRef, isVisible];
};

const Header = () => {
  const scrollToSection = useScrollToSection();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = window.location.pathname;

  const handleTryNow = React.useCallback(() => {
    trackButtonClick("try_now_header");
    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/auto-post";
    } else {
      sendToTelegram("Header");
      window.location.href = "/login";
    }
  }, []);

  const handleMenuClick = React.useCallback(
    (item) => {
      if (item.id === "ai-service") {
        sendToTelegram("Header", 'Click "Dịch Vụ AI"');
      }

      if (item.action) {
        trackButtonClick(`menu_${item.id}`);
        item.action();
        return;
      }

      if (location !== "/" && location !== "/landing-page") {
        window.location.href = `/#${item.id}`;
        return;
      }

      scrollToSection(item.id);
    },
    [location, scrollToSection]
  );

  const toggleMenu = React.useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const menuItems = React.useMemo(
    () => [
      { id: "home", label: "Trang Chủ" },
      { id: "about", label: "Giới Thiệu" },
      { id: "features", label: "Tính Năng" },
      // Ẩn Auto Lead trên header (Lead Conversion Engine)
      // {
      //   id: "lead-conversion-engine",
      //   label: "Auto Lead",
      //   badge: { text: "HOT", color: "bg-gradient-to-r from-red-600 to-orange-500" },
      // },

      {
        id: "pricing",
        label: "Bảng Giá",
        action: () => (window.location.href = "/pricing"),
      },
      {
        id: "news",
        label: "Tin Tức",
        action: () => (window.location.href = "/news"),
      },
      {
        id: "community-zalo",
        label: "Cộng Đồng",
        action: () => window.open("https://zalo.me/g/nwhx7ivthokpx20v1o5w", "_blank"),
        badge: { text: "HOT", color: "bg-gradient-to-r from-red-500 to-orange-500" },
      },
      { id: "contact", label: "Liên Hệ" },
      {
        id: "try-now",
        label: "Trải Nghiệm Ngay",
        action: handleTryNow,
        highlight: true,
      },
    ],
    [handleTryNow]
  );

  // Đường dẫn logo và fallback
  const logoPath = "./images/logo1.webp";
  const publicLogoPath = "/logo1.webp";
  const fallbackLogo =
    "https://res.cloudinary.com/dtqlhleqr/image/upload/v1753212323/autowork-logo_mmsrzb.webp";

  // Hàm xử lý lỗi tải logo
  const handleLogoError = (e) => {
    if (e.target.src.includes(logoPath)) {
      e.target.src = publicLogoPath;
    } else if (e.target.src.includes(publicLogoPath)) {
      e.target.src = fallbackLogo;
    }
    e.target.onerror = null;
  };

  return (
    <header className="bg-transparent md:bg-[#14389F] shadow-none md:shadow-lg fixed w-full top-0 z-50 transition-all duration-300">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDE2ek0zNiAzMHYySDI0di0yaDF6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-10 hidden md:block"></div>
      <div className="container mx-auto px-6 md:px-10 py-4">
        <div className="flex justify-between items-center relative">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = "/";
            }}
            className="hidden md:block transform hover:scale-105 transition-transform duration-300"
            aria-label="AutoWork Logo - Trang chủ"
          >
            <div className="h-10 flex items-center justify-center">
              <img
                src={require("./images/logo1.webp")}
                alt="AutoWork Logo"
                className="h-full w-auto object-contain"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                width="100"
                height="40"
                onError={handleLogoError}
              />
            </div>
          </a>

          {/* Mobile Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = "/";
            }}
            className="hidden md:hidden transform hover:scale-105 transition-transform duration-300"
            aria-label="AutoWork Logo - Trang chủ"
          >
            <div className="h-10 flex items-center justify-center">
              <img
                src={require("./images/logo1.webp")}
                alt="AutoWork Logo"
                className="h-full w-auto object-contain"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                width="100"
                height="40"
                onError={handleLogoError}
              />
            </div>
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center justify-end flex-1">
            <nav className="flex items-center space-x-4 lg:space-x-8">
              {menuItems
                .filter((item) => !item.highlight && item.id !== "contact")
                .map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuClick(item);
                    }}
                    className="text-white hover:text-blue-400 transition-colors duration-300 font-medium text-sm lg:text-base whitespace-nowrap relative"
                  >
                    <span className="relative inline-block pr-1">
                      {item.label}
                      {item.badge && (
                        <span
                          className={`absolute -top-3.5 -right-2 ${item.badge.color} text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-md ring-1 ring-white/50 animate-pulse whitespace-nowrap leading-none`}
                        >
                          {item.badge.text}
                        </span>
                      )}
                    </span>
                  </a>
                ))}

              {/* Link Liên Hệ */}
              {menuItems
                .filter((item) => item.id === "contact")
                .map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuClick(item);
                    }}
                    className="text-white hover:text-blue-400 transition-colors duration-300 font-medium text-sm lg:text-base whitespace-nowrap relative"
                  >
                    {item.label}
                  </a>
                ))}

              {/* Nút chọn ngôn ngữ */}
              <div>
                <LanguageSelector isInHeader={true} />
              </div>

              {/* Button Trải Nghiệm Ngay */}
              {menuItems
                .filter((item) => item.highlight)
                .map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuClick(item);
                    }}
                    className="bg-white text-blue-600 px-3 lg:px-4 py-2 rounded-full hover:bg-opacity-90 transition-colors duration-300 font-medium text-sm lg:text-base whitespace-nowrap"
                  >
                    {item.label}
                  </a>
                ))}
            </nav>
          </div>

          {/* Placeholder for Mobile - to keep space on the left */}
          <div className="md:hidden flex-1"></div>

          {/* Mobile Menu Button */}
          <div className="flex items-center">
            {/* Nút chọn ngôn ngữ trên mobile */}
            <div className="md:hidden mr-2">
              <div className="language-btn-landing-mobile">
                <LanguageSelector isInHeader={true} />
              </div>
            </div>

            <button
              type="button"
              onClick={toggleMenu}
              className="md:hidden p-2 rounded-lg text-gray-800 hover:bg-gray-100/20 focus:outline-none ml-auto"
              aria-expanded={isMenuOpen}
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 mt-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg">
            <div className="flex flex-col space-y-1">
              {menuItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick(item);
                    setIsMenuOpen(false);
                  }}
                  className={`transition-colors duration-300 font-medium px-4 py-2 ${
                    item.highlight
                      ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                      : "text-gray-800 hover:bg-gray-100"
                  } relative`}
                >
                  {item.label}
                  {item.badge && (
                    <span
                      className={`ml-2 inline-block ${item.badge.color} text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full font-bold shadow-sm animate-pulse whitespace-nowrap`}
                    >
                      {item.badge.text}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

const HeroSection = () => {
  const handleTryNow = React.useCallback(() => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "hero_section" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_button", { location: "hero_section" });

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/auto-post";
    } else {
      sendToTelegram("Hero Section");
      window.location.href = "/login";
    }
  }, []);

  const handleWatchVideo = () => {
    trackVideoView("tutorial_video");
  };

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-gradient-to-b from-blue-700 to-blue-600 min-h-screen flex items-center"
      aria-label="Hero Section"
    >
      {/* Background effect */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-pattern opacity-10"></div>
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400 rounded-full filter blur-3xl opacity-20"></div>
      </div>

      <div className="container mx-auto px-6 md:px-10 lg:px-20 xl:px-40 py-8 md:py-16 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <span className="inline-block px-4 py-2.5 md:px-5 md:py-3 bg-blue-800/50 text-blue-100 rounded-full text-sm md:text-base lg:text-lg font-medium backdrop-blur-sm border border-blue-400/20">
                Nền Tảng AI Viết & Đăng Bài Đa Kênh Cho Fanpage, Website, Zalo OA
              </span>

              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                <span className="block text-white">
                  AI Tự Động Viết Và Đăng Bài
                </span>
                <span className="highlight-text bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-100">
                  Cho Facebook, WordPress, Zalo OA
                </span>
              </h1>

              <div className="flex flex-wrap gap-3 mt-4">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-200 border border-blue-400/30 rounded-full text-sm font-semibold flex items-center gap-1.5 shadow-sm">
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                  Chuyên dùng cho Fanpage
                </span>
                <span className="px-3 py-1 bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 rounded-full text-sm font-semibold flex items-center gap-1.5 shadow-sm">
                  <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                  Chuyên dùng cho Website
                </span>
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-100 border border-cyan-300/30 rounded-full text-sm font-semibold flex items-center gap-1.5 shadow-sm">
                  <svg className="w-4 h-4 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v10H8l-4 4V5z" /></svg>
                  Hỗ trợ Zalo OA
                </span>
              </div>

              <p className="text-base md:text-lg lg:text-xl text-blue-50 max-w-xl mt-4">
                Tạo content bán hàng, bài SEO website, bài Zalo OA và auto
                comment Fanpage trong một hệ thống quản lý tập trung.
                <span className="font-semibold text-white ml-1">
                  Tiết kiệm đến 80% thời gian cho marketing automation AI.
                </span>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={handleTryNow}
                className="px-6 py-3 md:px-8 md:py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-xl font-bold text-base md:text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center relative"
                aria-label="Trải nghiệm miễn phí ngay"
              >
                Trải Nghiệm Ngay
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 ml-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs md:text-sm px-2 py-1 rounded-full font-bold animate-pulse">
                  Miễn phí
                </span>
              </button>

              <a
                href="https://www.youtube.com/watch?v=6aJZvUsdl-s&t=5s&ab_channel=AUTOWORK"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWatchVideo}
                className="px-5 py-3 md:px-6 md:py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-medium text-sm md:text-base hover:bg-white/20 transition-all duration-300 flex items-center border border-white/10"
                aria-label="Xem video hướng dẫn sử dụng AutoWork"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                Xem Video Hướng Dẫn
              </a>
            </div>

            {/* Zalo Community Quick Link */}
            <div className="pt-1 pb-3 flex items-center gap-2 text-blue-100 text-sm md:text-base">
              <span className="flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <span>Ứng dụng AI vào công việc hằng ngày?</span>
              <a 
                href="https://zalo.me/g/nwhx7ivthokpx20v1o5w" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yellow-300 hover:text-yellow-200 underline font-semibold transition-colors duration-300 flex items-center gap-1"
              >
                Tham gia ngay Kênh chia sẻ Zalo từ AutoWork! 🚀
              </a>
            </div>

            <div className="pt-4 border-t border-blue-400/20">
              <div className="flex items-center space-x-6">
                <div className="flex -space-x-2">
                  {[
                    "estate.png",
                    "fashion.png",
                    "pharmacy-658.jpg",
                    "travel.png",
                  ].map((logo, i) => (
                    <div
                      key={i}
                      className="w-12 h-12 rounded-full border-2 border-blue-600 overflow-hidden bg-white flex items-center justify-center shadow-md hover:scale-110 transition-transform duration-300"
                      style={{ zIndex: 4 - i }}
                    >
                      <img
                        src={require(`./images/company/${logo}`)}
                        alt={`Logo đối tác ${i + 1}`}
                        className="w-9 h-9 object-contain"
                        loading="lazy"
                        width="36"
                        height="36"
                        decoding="async"
                      />
                    </div>
                  ))}
                </div>
                <div className="text-blue-50">
                  <p className="font-medium">
                    Đã có hơn{" "}
                    <span className="text-white font-bold">1000+</span> đối tác
                    tin tưởng và sử dụng dịch vụ
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative max-w-lg md:max-w-xl lg:max-w-3xl mx-auto">
            <div className="absolute -inset-4 bg-blue-600/30 rounded-2xl blur-lg"></div>
            <div className="relative overflow-hidden rounded-2xl border border-white/20 shadow-2xl">
              <img
                src="https://res.cloudinary.com/dtqlhleqr/image/upload/v1777870707/AutoWork%20-%20MKT/ChatGPT_Image_11_57_49_4_thg_5_2026_z3lfeo.webp"
                alt="Giao diện AutoWork AI tự động đăng bài đa kênh cho Facebook WordPress và Zalo OA"
                className="w-full h-auto rounded-2xl transform hover:scale-105 transition-transform duration-700"
                loading="lazy"
                decoding="async"
                fetchPriority="high"
              />


            </div>

            <div className="absolute -right-4 md:-right-8 -bottom-4 md:-bottom-8 bg-yellow-400 text-gray-900 font-bold px-3 md:px-6 py-1 md:py-2 rounded-lg shadow-lg transform rotate-12 text-sm md:text-base z-10">
              Tiết kiệm 80% thời gian!
            </div>
          </div>
        </div>
      </div>

      {/* Wave Bottom */}
      <div className="absolute bottom-0 left-0 w-full transform scale-y-50">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          className="w-full"
          aria-hidden="true"
        >
          <path
            fill="#ffffff"
            fillOpacity="1"
            d="M0,128L48,122.7C96,117,192,107,288,101.3C384,96,480,96,576,112C672,128,768,160,864,165.3C960,171,1056,149,1152,133.3C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </div>
    </section>
  );
};

const PainPointsSection = () => {
  const [ref, isVisible] = useScrollAnimationOnce();
  const painPoints = [
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-10 w-10"
        >
          <path d="M12 5v14" strokeLinecap="round" />
          <path d="M5 12h14" strokeLinecap="round" />
          <path d="M7 7l10 10" strokeLinecap="round" />
          <path d="M17 7L7 17" strokeLinecap="round" />
        </svg>
      ),
      title: "Bế tắc ý tưởng nội dung?",
      description:
        "Mất hàng giờ để nghĩ ra một bài đăng hấp dẫn? Bài viết không thu hút tương tác dù đã rất cố gắng?",
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-10 w-10"
        >
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4l3 3" strokeLinecap="round" />
        </svg>
      ),
      title: "Bỏ lỡ thời điểm vàng?",
      description:
        "Đăng bài thủ công khiến bạn bỏ lỡ giờ vàng, bài viết chìm nghỉm dù nội dung chất lượng?",
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-10 w-10"
        >
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v10M9 10h6" strokeLinecap="round" />
        </svg>
      ),
      title: "Chi phí marketing cao ngất?",
      description:
        "Tốn kém chi phí thuê nhân sự, agency nhưng hiệu quả không như mong đợi?",
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-10 w-10"
        >
          <path
            d="M4 4h16v16H4z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M4 8h16M8 4v16" strokeLinecap="round" />
          <path d="M12 12l4 4" strokeLinecap="round" />
          <path d="M16 12l-4 4" strokeLinecap="round" />
        </svg>
      ),
      title: "Website 'Đóng Bụi', Thiếu Nội Dung Tươi Mới?",
      description:
        "Mất hàng giờ để viết bài, tối ưu SEO và đăng lên website? Nội dung nghèo nàn khiến website mãi không lên top Google?",
    },
  ];

  const centerPainPoints = [
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-10 w-10"
        >
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M8 8h8M8 12h6M8 16h8" strokeLinecap="round" />
        </svg>
      ),
      title: "Quá tải với nhiều Fanpage?",
      description:
        "Đau đầu với việc quản lý nhiều trang cùng lúc? Không thể theo dõi và tương tác kịp thời với khách hàng?",
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-10 w-10"
        >
          <circle cx="12" cy="12" r="8" />
          <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
        </svg>
      ),
      title: "Thiếu hiện diện trên Zalo OA?",
      description:
        "Muốn chăm sóc khách hàng trên Zalo OA nhưng vẫn phải viết bài, chọn ảnh và đăng thủ công từng nội dung?",
    },
  ];

  return (
    <section
      id="pain-points"
      ref={ref}
      className={`py-12 md:py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-red-50 to-transparent opacity-50"></div>
      <div className="container mx-auto px-6 md:px-10 relative">
        <div className="text-center max-w-4xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bạn Đang Gặp Những Vấn Đề Này?
            <div className="h-1 w-20 bg-red-600 mx-auto mt-4"></div>
          </h2>
          <p className="text-gray-600 text-lg">
            Hãy để AutoWork giúp bạn giải quyết tất cả những khó khăn trong việc
            quản lý content cho Facebook Fanpage, WordPress và Zalo OA
          </p>
        </div>

        {/* Pain points grid - 6 items in 2 rows */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[...painPoints, ...centerPainPoints].map((point, index) => (
            <div
              key={index}
              className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div
                className={`mb-6 flex h-20 w-20 items-center justify-center rounded-full ${
                  index === 0
                    ? "bg-gradient-to-br from-purple-500 via-pink-500 to-red-500"
                    : index === 1
                    ? "bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500"
                    : index === 2
                    ? "bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500"
                    : index === 3
                    ? "bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500"
                    : index === 4
                    ? "bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500"
                    : "bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500"
                } transform hover:scale-110 transition-transform duration-300 mx-auto text-white shadow-lg`}
              >
                {point.icon}
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-800">
                {point.title}
              </h3>
              <p className="text-gray-600 text-center">{point.description}</p>
            </div>
          ))}
        </div>

        <div
          className={`mt-12 text-center transform ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
          style={{ transitionDelay: "800ms" }}
        >
          <button
            onClick={() => {
              const userAccessToken = localStorage.getItem("userAccessToken");
              if (userAccessToken) {
                window.location.href = "/auto-post";
              } else {
                window.location.href = "/login";
              }
            }}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white rounded-full hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
          >
            Giải Quyết Ngay
            <svg
              className="w-5 h-5 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  const scrollToSection = useScrollToSection();
  const [ref, isVisible] = useScrollAnimationOnce();

  return (
    <section
      id="about"
      ref={ref}
      className={`py-20 bg-white relative overflow-hidden transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="md:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://res.cloudinary.com/dtqlhleqr/image/upload/v1779002901/ChatGPT_Image_14_27_43_17_thg_5_2026_rx2ah5.webp"
                alt="Giải pháp AI tự động viết và đăng bài đa kênh"
                className="w-full"
                loading="lazy"
              />
            </div>
          </div>

          <div className="md:w-1/2 space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Giải Pháp AI Content Toàn Diện
              <span className="block text-blue-600 mt-2">Autowork</span>
            </h2>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Tự Động Hóa Content Đa Kênh
                  </h3>
                  <p className="text-gray-600">
                    AI tự động đề xuất ý tưởng, viết content, tối ưu SEO cơ bản,
                    lên lịch đăng bài cho Facebook Fanpage, WordPress/WooCommerce
                    và Zalo OA
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Tiết Kiệm Thời Gian
                  </h3>
                  <p className="text-gray-600">
                    Giảm hơn 80% thời gian quản lý cho cả các nền tảng với công
                    nghệ AI tiên tiến
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Tăng Hiệu Quả
                  </h3>
                  <p className="text-gray-600">
                    Tăng hiệu quả tương tác, traffic website và chất lượng lead
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <a
                href="#features"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection("features");
                }}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300"
              >
                Xem Tính Năng
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Lead Conversion Engine — tạm ẩn (không render section; code UI gốc giữ nguyên dưới dạng comment)
const LeadConversionEngineSection = () => null;

// Lead Conversion Engine Section - Auto Comment Highlight (UI đã tắt)
// const LeadConversionEngineSection = () => {
//   const [ref, isVisible] = useScrollAnimationOnce();

//   return (
//     <section
//       id="lead-conversion-engine"
//       ref={ref}
//       className={`py-20 md:py-28 bg-white relative overflow-hidden transition-all duration-1000 transform ${
//         isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
//       }`}
//     >
//       <div className="container mx-auto px-6 md:px-8 max-w-[1400px] relative z-10">
//         <div className="text-center max-w-4xl mx-auto mb-16 space-y-6">
//           <div className="inline-flex items-center gap-2">
//             <span className="inline-flex items-center px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-full text-sm font-bold shadow-sm">
//               <span className="relative flex items-center gap-2">
//                 <span className="animate-pulse">❤️‍🔥</span>
//                 <span>Lead Conversion Engine</span>
//               </span>
//             </span>
//           </div>
//           <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-800 leading-tight tracking-tight">
//             Tự Động Ra Đơn
//             <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mt-2">Từ Comment Facebook</span>
//           </h2>
//           <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
//             AI tự động nhận diện người đang muốn mua, ưu tiên lead nóng, và đẩy cơ hội chốt đơn đến team sale của bạn qua Telegram ngay lập tức. Khách sẽ không phải đợi!
//           </p>
//         </div>

//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-[1200px] mx-auto">
//           {[
//             {
//               title: "Không Bỏ Lỡ Khách Hỏi Mua",
//               description: "AI quét và nhận diện ngay lập tức mọi comment có ý định mua hàng, đảm bảo không một cơ hội nào bị bỏ sót.",
//               icon: (
//                 <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
//                 </svg>
//               ),
//               color: "text-blue-600",
//               bgColor: "bg-blue-50",
//               borderColor: "border-blue-100",
//             },
//             {
//               title: "Phân Loại Lead Nóng Lập Tức",
//               description: "Hệ thống AI hai lớp tự động phân loại độ nóng của từng khách hàng (Nóng/Ấm/Lạnh) để ưu tiên chốt sale.",
//               icon: (
//                 <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
//                 </svg>
//               ),
//               color: "text-orange-600",
//               bgColor: "bg-orange-50",
//               borderColor: "border-orange-100",
//             },
//             {
//               title: "Bắn Alert Real-time Telegram",
//               description: "Team Sale nhận ngay thông báo về Telegram khi có Lead Nóng cần follow-up gấp. Vào điểm và chốt!",
//               icon: (
//                 <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
//                 </svg>
//               ),
//               color: "text-emerald-600",
//               bgColor: "bg-emerald-50",
//               borderColor: "border-emerald-100",
//             },
//             {
//               title: "Dashboard Ra Đơn Chuyên Sâu",
//               description: "Nhìn thấy ngay trang nào, bài viết nào đang đem lại đỉnh điểm cơ hội chốt đơn để phân bổ ngân sách quảng cáo.",
//               icon: (
//                 <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
//                 </svg>
//               ),
//               color: "text-purple-600",
//               bgColor: "bg-purple-50",
//               borderColor: "border-purple-100",
//             }
//           ].map((item, index) => (
//             <div
//               key={index}
//               className={`bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border ${item.borderColor} group ${
//                 isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
//               }`}
//               style={{ transitionDelay: `${index * 150}ms` }}
//             >
//               <div
//                 className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${item.bgColor} ${item.color} transform group-hover:scale-110 transition-transform duration-300`}
//               >
//                 {item.icon}
//               </div>
//               <h3 className="text-xl font-bold mb-4 text-slate-800">
//                 {item.title}
//               </h3>
//               <p className="text-slate-600 leading-relaxed font-medium">
//                 {item.description}
//               </p>
//             </div>
//           ))}
//         </div>

//         {/* Comparison: AutoWork vs Traditional Tools */}
//         <div 
//           className={`mt-24 max-w-[1050px] mx-auto transform transition-all duration-1000 ${
//             isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
//           }`}
//           style={{ transitionDelay: "400ms" }}
//         >
//           <div className="text-center mb-10 md:mb-14">
//             <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-4">
//               <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
//               </svg>
//               So Sánh Nền Tảng
//             </div>
//             <h3 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
//               Tại Sao Chọn <span className="text-blue-600">AutoWork?</span>
//             </h3>
//             <p className="text-slate-500 max-w-2xl mx-auto text-base md:text-lg">
//               So sánh AutoWork với các nền tảng tự động hóa phổ biến khác trên thị trường
//             </p>
//           </div>

//           <div className="grid md:grid-cols-2 gap-0 border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
//             {/* Traditional Tools */}
//             <div className="bg-slate-50 p-8 md:p-10 border-b md:border-b-0 md:border-r border-slate-200">
//               <div className="flex items-center gap-3 mb-6">
//                 <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200 text-slate-500 shadow-inner">
//                   <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                   </svg>
//                 </div>
//                 <h4 className="text-xl font-bold text-slate-600">Tool Auto Comment Cũ</h4>
//               </div>
//               <ul className="space-y-5">
//                 {[
//                   "Phải cấu hình từ khoá phức tạp, mất nhiều thời gian test.",
//                   "Khách viết sai chính tả, viết tắt là tool bó tay, bỏ sót khách mua.",
//                   "Trả lời máy móc như robot, gây khó chịu cho khách hàng.",
//                   "Sales tự 'mò mẫm' lội lại comment để tìm khách nóng.",
//                   "Lộ data khách hàng cho đối thủ vì không kịp ẩn bình luận."
//                 ].map((item, idx) => (
//                   <li key={idx} className="flex items-start gap-3 text-slate-600">
//                     <svg className="w-5 h-5 mt-0.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                     </svg>
//                     <span>{item}</span>
//                   </li>
//                 ))}
//               </ul>
//             </div>

//             {/* AutoWork AI */}
//             <div className="bg-blue-50/50 p-8 md:p-10 relative overflow-hidden">
//               <div className="absolute top-0 right-0 w-48 h-48 bg-blue-100 rounded-full blur-3xl opacity-50 -mr-16 -mt-16 animate-pulse"></div>
//               <div className="flex items-center gap-3 mb-6 relative z-10">
//                 <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 shadow-sm border border-blue-200">
//                   <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
//                   </svg>
//                 </div>
//                 <h4 className="text-xl font-bold text-slate-800">AutoWork Lead Engine</h4>
//               </div>
//               <ul className="space-y-5 relative z-10">
//                 {[
//                   "Không cần set từ khoá, AI tự nhận diện kịch bản tư vấn.",
//                   "Hiểu chính xác 100% ý định mua hàng dù khách viết sai chính tả.",
//                   "Tự sinh câu trả lời linh hoạt, tự nhiên như nhân viên Sales.",
//                   "Chấm nhiệt độ Lead lập tức, gửi Alert khách Nóng về Telegram.",
//                   "Hệ thống hoạt động 24/7, quản lý và vận hành hàng loạt Fanpage tập trung."
//                 ].map((item, idx) => (
//                   <li key={idx} className="flex items-start gap-3 text-slate-800 font-medium">
//                     <svg className="w-5 h-5 mt-0.5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
//                     </svg>
//                     <span>{item}</span>
//                   </li>
//                 ))}
//               </ul>
//             </div>
//           </div>
//         </div>

//         <div
//           className={`mt-20 text-center transform ${
//             isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
//           }`}
//           style={{ transitionDelay: "800ms" }}
//         >
//           <button
//             onClick={() => {
//               const userAccessToken = localStorage.getItem("userAccessToken");
//               if (userAccessToken) {
//                 window.location.href = "/facebook-comment-config";
//               } else {
//                 sendToTelegram("LeadConversionEngineSection");
//                 window.location.href = "/login";
//               }
//             }}
//             className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
//           >
//             Trải Nghiệm Ngay Lead Engine
//             <svg
//               className="w-5 h-5 ml-2"
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth="2.5"
//                 d="M17 8l4 4m0 0l-4 4m4-4H3"
//               />
//             </svg>
//           </button>
//         </div>
//       </div>
//     </section>
//   );
// };

// App Service Section - New Feature
const AIAppServiceSection = () => {
  const [ref, isVisible] = useScrollAnimationOnce();

  const appExamples = [
    {
      title: "App Tạo Ảnh Marketing",
      description:
        "Tự động tạo ảnh quảng cáo sản phẩm, banner, poster chuyên nghiệp chỉ với mô tả văn bản đơn giản.",
      icon: (
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          className="w-7 h-7"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      color: "from-purple-500 via-indigo-500 to-purple-600",
      glowColor: "purple",
    },
    {
      title: "App Tạo Giọng Đọc AI",
      description:
        "Chuyển đổi văn bản thành giọng đọc tự nhiên, cảm xúc cho video TikTok, Youtube, Podcast.",
      icon: (
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          className="w-7 h-7"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
      ),
      color: "from-pink-500 via-rose-500 to-pink-600",
      glowColor: "pink",
    },
    {
      title: "Tóm Tắt & Phân Tích",
      description:
        "Tự động tóm tắt nội dung video, tài liệu dài, phân tích dữ liệu đối thủ cạnh tranh.",
      icon: (
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          className="w-7 h-7"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      ),
      color: "from-emerald-500 via-teal-500 to-emerald-600",
      glowColor: "emerald",
    },
    {
      title: "Các Ứng Dụng Đa Dạng Khác",
      description:
        "Tùy biến tạo mọi loại ứng dụng AI theo nhu cầu riêng: Phân tích dữ liệu, Tóm tắt tin tức, Dịch thuật, Viết code...",
      icon: (
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          className="w-7 h-7"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      ),
      color: "from-blue-500 via-cyan-500 to-blue-600",
      glowColor: "cyan",
    },
  ];

  return (
    <section
      id="ai-service"
      className={`py-20 md:py-32 bg-gradient-to-b from-[#0A0E27] via-[#0F172A] to-[#0A0E27] relative overflow-hidden transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      ref={ref}
    >
      {/* Enhanced Background Effects with Animation */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute -top-[15%] -left-[8%] w-[60%] h-[60%] bg-gradient-to-br from-purple-600/40 via-purple-500/30 to-transparent rounded-full blur-[120px] animate-pulse"></div>
        <div
          className="absolute top-[25%] -right-[8%] w-[50%] h-[50%] bg-gradient-to-br from-blue-600/40 via-cyan-500/30 to-transparent rounded-full blur-[120px] animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute bottom-[10%] left-[20%] w-[40%] h-[40%] bg-gradient-to-br from-indigo-600/30 via-purple-500/20 to-transparent rounded-full blur-[100px] animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px] opacity-30"></div>
      </div>

      <div className="container mx-auto px-6 md:px-8 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content Section */}
          <div className="space-y-8 lg:space-y-10">
            <div className="space-y-6">
              {/* Badge */}
              <div className="inline-flex items-center gap-2">
                <span className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md text-white rounded-full text-sm font-bold shadow-lg shadow-purple-500/20 border border-white/10 relative overflow-hidden group animate-[bounce_2s_infinite]">
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-pink-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                  <span className="relative flex items-center gap-2">
                    <span className="text-xl animate-pulse">🔥</span>
                    <span>Dịch Vụ Mới</span>
                  </span>
                </span>
              </div>

              {/* Heading */}
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
                <span className="block mb-2">Huấn Luyện 1:1 & Làm App AI</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-[length:200%_auto] animate-[shimmer_3s_linear_infinite]">
                  Theo Yêu Cầu Riêng Biệt
                </span>
              </h2>

              {/* Description */}
              <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-xl">
                Giải pháp AI toàn diện: Tham gia khóa huấn luyện 1:1 để tự tay xây dựng
                ứng dụng, HOẶC đặt hàng chúng tôi phát triển App AI "may đo" riêng cho
                nhu cầu của bạn. Biến ý tưởng thành lợi thế cạnh tranh ngay hôm nay.
              </p>
            </div>

            {/* Features List */}
            <ul className="space-y-5">
              {[
                "Coaching 1:1 Trực Tiếp: 'Cầm Tay Chỉ Việc' Tận Tình Qua Zoom/Meet",
                "Làm App Theo Yêu Cầu: Đội ngũ chuyên gia xây dựng trọn gói ứng dụng AI theo đúng mong muốn của bạn",
                "Giáo Trình Thực Chiến: Giải quyết bài toán cụ thể của bạn (Marketing, Sale, HR...) ngay tại lớp",
                "Hỗ Trợ Sau Khóa Học: Đồng hành giải đáp & cập nhật công nghệ mới liên tục",
                "Tư Vấn Triển Khai: Hướng dẫn đóng gói sản phẩm để kinh doanh hoặc tối ưu quy trình nội bộ",
              ].map((item, index) => (
                <li
                  key={index}
                  className="flex items-start gap-4 text-gray-200 group/item"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible
                      ? "translateX(0)"
                      : "translateX(-20px)",
                    transition: `all 0.5s ease ${index * 100}ms`,
                  }}
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm border border-green-400/30 flex items-center justify-center group-hover/item:scale-110 group-hover/item:bg-gradient-to-br group-hover/item:from-green-500/30 group-hover/item:to-emerald-500/30 transition-all duration-300 shadow-lg shadow-green-500/20">
                    <svg
                      className="w-4 h-4 text-green-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span className="text-base md:text-lg leading-relaxed pt-0.5 group-hover/item:text-white transition-colors duration-300">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
              <a
                href="#contact"
                onClick={() => {
                  trackButtonClick("register_consultation_ai_master");
                  sendToTelegram(
                    "AI App Service Section",
                    'Click "Đăng Ký Tư Vấn Ngay"'
                  );
                }}
                className="group relative inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-[length:200%_auto] text-white rounded-2xl font-bold text-lg md:text-xl shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/50 transform hover:-translate-y-1 hover:scale-105 transition-all duration-300 overflow-hidden animate-[heartbeat_1.5s_ease-in-out_infinite]"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-[length:200%_auto] animate-[shimmer_3s_linear_infinite] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative flex items-center gap-2">
                  <span>Đăng Ký Tư Vấn Ngay</span>
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </div>

          {/* Right Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {appExamples.map((app, index) => (
              <div
                key={index}
                className="group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 lg:p-7 hover:bg-white/10 transition-all duration-500 overflow-hidden"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible
                    ? "translateY(0) scale(1)"
                    : "translateY(30px) scale(0.95)",
                  transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${
                    index * 150
                  }ms`,
                }}
              >
                {/* Glow Effect on Hover */}
                <div
                  className={`absolute -inset-0.5 bg-gradient-to-br ${app.color} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`}
                ></div>

                {/* Card Content */}
                <div className="relative z-10">
                  {/* Icon Container */}
                  <div
                    className={`relative mb-5 w-16 h-16 rounded-2xl bg-gradient-to-br ${app.color} flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
                    <div className="absolute inset-0 bg-gradient-to-br ${app.color} opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500 rounded-2xl"></div>
                    <div className="relative text-white">{app.icon}</div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl lg:text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-300">
                    {app.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 text-sm lg:text-base leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                    {app.description}
                  </p>

                  {/* Decorative Element */}
                  <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-white/5 to-transparent rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add shimmer and heartbeat animation keyframes via style tag */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes heartbeat {
          0% { transform: scale(1); }
          14% { transform: scale(1.05); }
          28% { transform: scale(1); }
          42% { transform: scale(1.05); }
          70% { transform: scale(1); }
        }
        @keyframes wave {
          0%, 100% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-3px) rotate(5deg); }
        }
      `}</style>
    </section>
  );
};

const FeaturesSection = () => {
  const [ref, isVisible] = useScrollAnimationOnce();

  return (
    <section
      id="features"
      ref={ref}
      className={`bg-gray-50 py-12 md:py-24 transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-[1400px]">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          Tính Năng Nổi Bật
        </h2>
        <h3 className="text-xl md:text-2xl text-center text-gray-600 mb-12">
          Khám phá sức mạnh của nền tảng quản lý nội dung đa kênh
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-[1200px] mx-auto">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`bg-white p-8 md:p-10 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{
                transitionDelay: `${index * 200}ms`,
              }}
            >
              <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 transform hover:scale-110 transition-transform duration-300 mx-auto">
                {React.cloneElement(feature.icon, {
                  className: "h-12 w-12 text-white",
                })}
              </div>
              <h3 className="text-2xl font-bold mb-5 text-center">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-center text-lg leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Comparison Section - AutoWork vs n8n, Make, Zapier
const ComparisonSection = () => {
  const [ref, isVisible] = useScrollAnimationOnce();

  const comparisonData = [
    {
      feature: "AI Viết Content",
      autowork: "Tích hợp sẵn, chuyên biệt",
      n8n: "Cần tự cấu hình node AI",
      make: "Cần kết nối app AI riêng",
      zapier: "Cần kết nối app AI riêng",
    },
    {
      feature: "Giá khởi điểm",
      autowork: "Miễn phí trải nghiệm",
      n8n: "Từ €24/tháng",
      make: "Từ $9/tháng",
      zapier: "Từ $29.99/tháng",
    },
    {
      feature: "Đăng bài tự động",
      autowork: "Facebook, WordPress, Zalo OA",
      n8n: "Cần build workflow phức tạp",
      make: "Cần build scenario nhiều bước",
      zapier: "Cần tạo Zap nhiều bước",
    },
    {
      feature: "Độ phức tạp sử dụng",
      autowork: "Cực kỳ đơn giản",
      n8n: "Cần kiến thức kỹ thuật",
      make: "Trung bình, cần học",
      zapier: "Đơn giản nhưng giới hạn",
    },
    {
      feature: "Hỗ trợ tiếng Việt",
      autowork: "Hoàn toàn",
      n8n: "Không",
      make: "Không",
      zapier: "Không",
      highlight: true,
    },
    {
      feature: "Chuyên biệt Content Marketing",
      autowork: "Facebook, SEO web, Zalo",
      n8n: "Đa mục đích chung",
      make: "Đa mục đích chung",
      zapier: "Đa mục đích chung",
      highlight: true,
    },
    {
      feature: "Lên lịch & Tối ưu giờ vàng",
      autowork: "Tự động thông minh",
      n8n: "Cần tự thiết lập",
      make: "Cần cấu hình thủ công",
      zapier: "Chỉ lên lịch cơ bản",
    },
  ];

  return (
    <section
      id="comparison"
      ref={ref}
      className={`py-16 md:py-28 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      {/* Subtle Background Blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute -top-[15%] -right-[10%] w-[45%] h-[45%] bg-gradient-to-br from-blue-100/60 via-blue-50/30 to-transparent rounded-full blur-[80px]"></div>
        <div className="absolute bottom-[5%] -left-[8%] w-[35%] h-[35%] bg-gradient-to-br from-indigo-100/40 via-blue-50/20 to-transparent rounded-full blur-[80px]"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 px-5 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-bold border border-blue-200 mb-5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            So Sánh Nền Tảng
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mt-4">
            Tại Sao Chọn{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              AutoWork?
            </span>
          </h2>
          <p className="text-gray-500 text-lg md:text-xl mt-4 max-w-2xl mx-auto">
            So sánh AutoWork với các nền tảng tự động hóa phổ biến khác trên thị trường
          </p>
        </div>

        {/* Comparison Table Card */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-300/50 border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              <div className="min-w-[720px]">
                {/* Table Header */}
                <div className="grid grid-cols-5 gap-0">
                  <div className="px-5 md:px-6 py-5 bg-gray-100 text-slate-500 text-xs font-bold uppercase tracking-widest flex items-center border-b-2 border-gray-200">
                    Tính năng
                  </div>
                  <div className="px-5 md:px-6 py-5 text-center bg-blue-600 border-b-2 border-blue-700">
                    <span className="text-white font-extrabold text-base md:text-lg tracking-wide">AutoWork</span>
                  </div>
                  <div className="px-5 md:px-6 py-5 text-center bg-gray-100 border-b-2 border-gray-200">
                    <span className="text-slate-600 font-bold text-sm md:text-base">n8n</span>
                  </div>
                  <div className="px-5 md:px-6 py-5 text-center bg-gray-100 border-b-2 border-gray-200">
                    <span className="text-slate-600 font-bold text-sm md:text-base">Make</span>
                  </div>
                  <div className="px-5 md:px-6 py-5 text-center bg-gray-100 border-b-2 border-gray-200">
                    <span className="text-slate-600 font-bold text-sm md:text-base">Zapier</span>
                  </div>
                </div>

                {/* Table Body */}
                {comparisonData.map((row, index) => (
                  <div
                    key={index}
                    className={`grid grid-cols-5 gap-0 border-b border-gray-200 transition-all duration-500 ${
                      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                    } ${index % 2 === 1 ? "bg-gray-50/60" : "bg-white"}`}
                    style={{ transitionDelay: `${200 + index * 80}ms` }}
                  >
                    {/* Feature Name */}
                    <div className={`px-5 md:px-6 py-4 md:py-5 flex items-center gap-2.5 ${
                      row.highlight ? "bg-amber-50" : ""
                    }`}>
                      {row.highlight && (
                        <span className="hidden md:inline-flex px-2 py-0.5 bg-amber-400 text-white text-[10px] font-bold rounded uppercase tracking-wider whitespace-nowrap shadow-sm">
                          Hot
                        </span>
                      )}
                      <span className="text-slate-900 font-semibold text-sm md:text-base">{row.feature}</span>
                    </div>

                    {/* AutoWork Column - Strong Highlight */}
                    <div className={`px-5 md:px-6 py-4 md:py-5 text-center bg-blue-50 border-l-2 border-r-2 border-blue-200 ${
                      row.highlight ? "bg-blue-100/80" : ""
                    }`}>
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="text-blue-800 font-bold text-sm md:text-base">{row.autowork}</span>
                        <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>

                    {/* Competitor Columns */}
                    {[row.n8n, row.make, row.zapier].map((value, i) => (
                      <div key={i} className={`px-5 md:px-6 py-4 md:py-5 text-center ${
                        row.highlight ? "bg-amber-50/50" : ""
                      }`}>
                        <span className="text-slate-500 text-sm md:text-base">{value}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-10 md:mt-14 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
          style={{ transitionDelay: "1000ms" }}
        >
          <button
            onClick={() => {
              const userAccessToken = localStorage.getItem("userAccessToken");
              if (userAccessToken) {
                window.location.href = "/auto-post";
              } else {
                sendToTelegram("Comparison Section");
                window.location.href = "/login";
              }
            }}
            className="group inline-flex items-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-bold text-lg md:text-xl shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/40 transform hover:-translate-y-1 transition-all duration-300 relative"
          >
            Trải Nghiệm Miễn Phí Ngay
            <svg className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
            <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
              FREE
            </span>
          </button>
          <p className="text-gray-400 text-sm mt-4">
            Không cần thẻ tín dụng • Bắt đầu ngay trong 2 phút
          </p>
        </div>
      </div>
    </section>
  );
};

const PartnerToolsOverviewSection = () => {
  const [ref, isVisible] = useScrollAnimationOnce();

  const partnerTools = [
    {
      slug: "fbtool",
      name: "FBTool.net",
      platform: "Facebook",
      tagline: "Chuyên sâu cho Group & Profile",
      description:
        "Trong khi AutoWork tập trung quản lý Fanpage, FBTool là giải pháp chuyên biệt để đăng bài hàng loạt lên Facebook Group & trang cá nhân, rải link và nuôi via.",
      refLink: "https://fbtool.net?refcode=autowork",
      color: "from-blue-500 to-indigo-600",
      glowColor: "group-hover:shadow-[0_0_40px_rgba(59,130,246,0.25)]",
      tagClass: "bg-blue-500/10 text-blue-300 border-blue-500/20 group-hover:bg-blue-500/20 group-hover:border-blue-500/30",
      buttonClass: "from-blue-600 to-indigo-600 shadow-blue-500/25",
      platformIcon: (
        <svg className="w-8 h-8 drop-shadow-md" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      highlights: ["Group Facebook", "AI Spin", "Tìm Khách Hàng"],
      stats: { main: "24/7", label: "Hoạt Động" },
    },
    {
      slug: "zalotool",
      name: "ZaloTool.net",
      platform: "Zalo",
      tagline: "Kết Bạn & Chăm Sóc Tự Động",
      description:
        "Kết bạn hàng loạt, gửi tin nhắn cho 20K liên hệ chỉ 1 click, kéo nhóm, hiện thành viên ẩn — hỗ trợ 10 tài khoản Zalo.",
      refLink: "https://zalotool.net?refcode=autowork",
      color: "from-cyan-400 to-blue-500",
      glowColor: "group-hover:shadow-[0_0_40px_rgba(6,182,212,0.25)]",
      tagClass: "bg-cyan-500/10 text-cyan-300 border-cyan-500/20 group-hover:bg-cyan-500/20 group-hover:border-cyan-500/30",
      buttonClass: "from-cyan-500 to-blue-600 shadow-cyan-500/25",
      platformIcon: (
        <svg className="w-8 h-8 drop-shadow-md" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.373 0 0 5.037 0 11.25c0 3.525 1.672 6.675 4.284 8.775-.117.675-.417 2.55-.483 2.944-.075.487.18.48.378.35.156-.103 2.484-1.687 3.51-2.381A13.425 13.425 0 0012 21.5c6.627 0 12-4.588 12-10.25S18.627 0 12 0zm5.79 14.869H14.3a.39.39 0 01-.39-.39V8.7a.39.39 0 01.78 0v5.39h3.1a.39.39 0 010 .78zM7.17 8.31a.39.39 0 01.39.39v5.78a.39.39 0 01-.78 0V8.7a.39.39 0 01.39-.39zm4.53 0a.39.39 0 01.33.18l2.73 4.2V8.7a.39.39 0 01.78 0v5.78a.39.39 0 01-.72.21l-2.73-4.2v3.99a.39.39 0 01-.78 0V8.7a.39.39 0 01.39-.39zM5.5 14.48a.39.39 0 010-.78h3.1V8.7a.39.39 0 01.78 0v5.39a.39.39 0 01-.39.39H5.5z" />
        </svg>
      ),
      highlights: ["Kết Bạn 20K", "10 Tài Khoản", "Kéo Nhóm"],
      stats: { main: "20K+", label: "Tin Nhắn / Giây" },
    },
    {
      slug: "mailingtool",
      name: "MailingTool.net",
      platform: "Gmail",
      tagline: "Email Marketing Tự Động",
      description:
        "Gửi email hàng loạt bằng Gmail, AI spin & cá nhân hoá nội dung từng email, theo dõi người nhận đã mở và click.",
      refLink: "https://mailingtool.net?refcode=autowork",
      color: "from-orange-500 to-red-500",
      glowColor: "group-hover:shadow-[0_0_40px_rgba(249,115,22,0.25)]",
      tagClass: "bg-orange-500/10 text-orange-300 border-orange-500/20 group-hover:bg-orange-500/20 group-hover:border-orange-500/30",
      buttonClass: "from-orange-500 to-red-600 shadow-orange-500/25",
      platformIcon: (
        <svg className="w-8 h-8 drop-shadow-md" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
        </svg>
      ),
      highlights: ["AI Spin Email", "Auto Switch Gmail", "Tracking"],
      stats: { main: "AI", label: "Cá Nhân Hóa" },
    },
  ];

  return (
    <section
      id="partner-tools"
      ref={ref}
      className={`py-20 md:py-32 bg-gradient-to-b from-[#0F172A] via-[#0A1628] to-[#0F172A] relative overflow-hidden transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      {/* Ambient Light Effects */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-[10%] -right-[10%] w-[55%] h-[55%] bg-gradient-to-br from-blue-600/20 via-indigo-500/10 to-transparent rounded-full blur-[140px] animate-pulse"></div>
        <div
          className="absolute bottom-[5%] -left-[10%] w-[45%] h-[45%] bg-gradient-to-br from-emerald-600/15 via-teal-500/10 to-transparent rounded-full blur-[140px] animate-pulse"
          style={{ animationDelay: "1.5s" }}
        ></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px] opacity-30"></div>
      </div>

      <div className="container mx-auto px-6 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-24">
          <span className="inline-flex items-center px-6 py-2.5 bg-white/5 backdrop-blur-xl text-white rounded-full text-sm font-semibold shadow-lg shadow-black/20 border border-white/10 mb-6 hover:bg-white/10 transition-colors duration-300">
            <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Hệ Sinh Thái Đối Tác
          </span>

          <h2 className="text-4xl md:text-5xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight mb-6">
            <span className="block mb-2 text-slate-200">Hệ Sinh Thái Phần Mềm</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-emerald-400 to-teal-400">
              Liên Kết Từ Các Đối Tác
            </span>
          </h2>

          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-medium">
            Bên cạnh <strong className="text-white font-bold">AutoWork</strong>, chúng tôi còn giới thiệu các công cụ chuyên biệt từ đối tác tin cậy giúp bạn tối ưu marketing toàn diện.
            <br/><span className="text-sm mt-2 block opacity-80">(Lưu ý: Đây là các dịch vụ của đối tác liên kết, hoạt động độc lập với AutoWork)</span>
          </p>
        </div>

        {/* Tool Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 max-w-7xl mx-auto">
          {partnerTools.map((tool, index) => (
            <div
              key={tool.slug}
              className={`group relative bg-gradient-to-b from-slate-800/80 to-slate-900/80 backdrop-blur-2xl border border-white/10 rounded-[2rem] hover:-translate-y-2 hover:border-white/25 transition-all duration-500 cursor-pointer shadow-2xl flex flex-col ${tool.glowColor}`}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible
                  ? "translateY(0) scale(1)"
                  : "translateY(30px) scale(0.95)",
                transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${
                  index * 150
                }ms`,
              }}
              onClick={() => {
                trackButtonClick(`partner_overview_${tool.slug}`);
                window.location.href = `/partner/${tool.slug}`;
              }}
            >
              {/* Top Accent Line */}
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Inner Ambient Glow */}
              <div
                className={`absolute -top-24 -right-24 w-64 h-64 bg-gradient-to-br ${tool.color} rounded-full blur-[80px] opacity-10 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none`}
              ></div>

              {/* Card Content Wrapper */}
              <div className="relative z-10 p-8 lg:p-10 flex flex-col h-full">
                
                {/* Header: Icon & Stats */}
                <div className="flex justify-between items-start mb-8">
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center shadow-lg shadow-black/20 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500 border border-white/20`}
                  >
                    <div className="text-white relative z-10">{tool.platformIcon}</div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-3xl font-black text-white tracking-tight drop-shadow-md">
                      {tool.stats.main}
                    </div>
                    <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider mt-1">
                      {tool.stats.label}
                    </div>
                  </div>
                </div>

                {/* Body: Title & Description */}
                <div className="mb-8 flex-grow">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-slate-300 transition-all duration-300">
                      {tool.name}
                    </h3>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full bg-white/10 text-slate-300 border border-white/20 whitespace-nowrap shadow-sm">
                      Đối tác
                    </span>
                  </div>
                  <div className={`inline-block px-3 py-1 mb-4 rounded-md text-xs font-bold tracking-wide uppercase bg-gradient-to-r ${tool.color} text-transparent bg-clip-text border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]`}>
                    {tool.tagline}
                  </div>
                  <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300 text-[15px]">
                    {tool.description}
                  </p>
                </div>

                {/* Highlights */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {tool.highlights.map((tag, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold backdrop-blur-sm border transition-colors duration-300 ${tool.tagClass}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Footer CTA */}
                <div className="mt-auto pt-6 flex items-center justify-between border-t border-white/5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      trackButtonClick(`partner_detail_${tool.slug}`);
                      window.location.href = `/partner/${tool.slug}`;
                    }}
                    className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${tool.buttonClass} text-white rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-white/10`}
                  >
                    Xem chi tiết
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <a
                    href={tool.refLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      trackButtonClick(`partner_try_${tool.slug}`);
                      sendToTelegram("Partner Overview", `Click "Dùng thử" ${tool.name}`);
                    }}
                    className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm font-semibold transition-colors duration-300 pr-2"
                  >
                    Dùng thử ngay
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>

              </div>
            </div>
          ))}
        </div>

        {/* Bottom Formula */}
        <div className="text-center mt-20 relative z-10">
          <div className="inline-block p-[1px] bg-gradient-to-r from-blue-500/30 via-emerald-500/30 to-teal-500/30 rounded-2xl">
            <div className="bg-slate-900/80 backdrop-blur-xl px-8 py-5 rounded-2xl border border-white/5 shadow-2xl">
              <p className="text-slate-400 text-lg mb-1 flex justify-center items-center gap-2 flex-wrap font-medium">
                <span className="text-white font-bold">AutoWork</span>
                <span className="text-slate-500">+</span> Các công cụ đối tác (FBTool, ZaloTool...)
              </p>
              <p className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-emerald-400 to-teal-400 font-extrabold text-xl md:text-2xl mt-2 tracking-tight">
                = Giải Pháp Marketing Đa Nền Tảng Toàn Diện
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const [ref, isVisible] = useScrollAnimationOnce();

  const testimonials = [
    {
      content:
        "Tiết kiệm được rất nhiều thời gian viết content, tương tác fanpage tăng 200% chỉ sau 1 tháng sử dụng.",
      author: "Chị Hương",
      role: "Shop thời trang",
    },
    {
      content:
        "AutoWork giúp tôi quản lý 5 fanpage cùng lúc một cách hiệu quả, doanh thu tăng 150%.",
      author: "Anh Tuấn",
      role: "Agency Marketing",
    },
    {
      content:
        "AI viết content rất tự nhiên và hấp dẫn. Tỷ lệ tương tác bài viết tăng gấp 3 lần, đơn hàng về đều đặn mỗi ngày.",
      author: "Chị Lan",
      role: "Spa & Beauty",
    },
    {
      content:
        "Trước đây tốn 2-3 tiếng mỗi ngày để lên content và đăng bài, giờ chỉ mất 15 phút setup là xong. Thực sự đáng đồng tiền!",
      author: "Anh Nam",
      role: "Cửa hàng nội thất",
    },
  ];

  const handleTryNow = () => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "pricing_section" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_button", { location: "pricing_section" });

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/auto-post";
    } else {
      window.location.href = "/login";
    }
  };

  return (
    <section
      ref={ref}
      className={`py-12 md:py-24 bg-gradient-to-b from-white to-gray-50 transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="container mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-16 relative">
          Khách Hàng Nói Gì Về Chúng Tôi?
          <div className="h-1 w-20 bg-blue-600 mx-auto mt-4"></div>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{
                transitionDelay: `${index * 200}ms`,
              }}
            >
              <div className="flex items-start mb-4">
                <svg
                  className="w-8 h-8 text-blue-600 mr-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-600 italic">{testimonial.content}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-800 font-medium">
                  {testimonial.author}
                </p>
                <p className="text-gray-500 text-sm">{testimonial.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AffiliateSection = () => {
  const [ref, isVisible] = useScrollAnimationOnce();

  const handleStartAffiliate = () => {
    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/affiliate";
    } else {
      window.location.href = "/login?redirect=affiliate-dashboard";
    }
  };

  return (
    <section
      id="affiliate"
      ref={ref}
      className={`py-16 md:py-24 bg-gradient-to-br from-indigo-50 via-white to-blue-50 relative overflow-hidden transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      {/* Decoration elements */}
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-400 rounded-full filter blur-3xl opacity-10"></div>
      <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-indigo-400 rounded-full filter blur-3xl opacity-10"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Chương Trình Affiliate
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trao Giải Pháp Hay, Nhận Ngay Thu Nhập
              <div className="h-1 w-20 bg-blue-600 mx-auto mt-4"></div>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Chia sẻ AutoWork và nhận{" "}
              <strong className="text-blue-600">30% hoa hồng</strong> trên mỗi
              giao dịch trong suốt{" "}
              <strong className="text-blue-600">1 năm</strong>
            </p>
          </div>

          {/* Hero Stats */}
          <div
            className={`grid grid-cols-3 gap-4 md:gap-6 mb-12 transform ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            } transition-all duration-1000 delay-200`}
          >
            {[
              {
                value: "30%",
                label: "Hoa hồng",
                sub: "cả Mua Plan & Mua Điểm",
              },
              {
                value: "12",
                label: "Tháng",
                sub: "thời hạn tính hoa hồng",
              },
              {
                value: "∞",
                label: "Không giới hạn",
                sub: "số người giới thiệu",
              },
            ].map((stat, i) => (
              <div
                key={i}
                className="text-center bg-white rounded-2xl p-5 md:p-6 shadow-lg border border-blue-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="text-3xl md:text-4xl font-extrabold text-blue-600 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm font-bold text-gray-800">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-500 mt-1">{stat.sub}</div>
              </div>
            ))}
          </div>

          {/* Two Column Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch mb-0">
            {/* Left Card — Benefits */}
            <div
              className={`transform ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-10 opacity-0"
              } transition-all duration-1000 delay-300`}
            >
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-blue-100 h-full flex flex-col">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Thu Nhập Thụ Động Không Giới Hạn
                </h3>
                <p className="text-gray-500 text-sm mb-5">
                  Miễn phí tham gia • Không cần đầu tư
                </p>
                <ul className="space-y-4 flex-grow">
                  {[
                    {
                      text: "Hoa hồng 30% trên mỗi giao dịch thành công",
                      highlight: true,
                    },
                    {
                      text: "Áp dụng cho cả Mua Plan lẫn Mua Điểm",
                      highlight: true,
                    },
                    {
                      text: "Thời hạn 1 năm — khách mua lại, bạn vẫn nhận tiền",
                      highlight: true,
                    },
                    {
                      text: "Cookie 30 ngày — click hôm nay, mua sau vẫn tính",
                    },
                    { text: "Không giới hạn số người giới thiệu" },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                          item.highlight ? "bg-blue-100" : "bg-green-100"
                        }`}
                      >
                        <svg
                          className={`w-4 h-4 ${
                            item.highlight
                              ? "text-blue-500"
                              : "text-green-500"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span
                        className={`${
                          item.highlight
                            ? "text-gray-900 font-semibold"
                            : "text-gray-700"
                        }`}
                      >
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Card — Steps + Income */}
            <div
              className={`transform ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "translate-x-10 opacity-0"
              } transition-all duration-1000 delay-500`}
            >
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 rounded-2xl shadow-xl h-full flex flex-col">
                <h3 className="text-2xl font-bold mb-6">
                  Chỉ 2 Bước Đơn Giản
                </h3>

                <div className="space-y-5 mb-6">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold">
                      1
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold">Chia Sẻ Link</h4>
                      <p className="text-blue-100 text-sm">
                        Gửi link giới thiệu qua Facebook, Zalo, Email — bất kỳ
                        đâu
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold">
                      2
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold">
                        Nhận Hoa Hồng 30%
                      </h4>
                      <p className="text-blue-100 text-sm">
                        Tự động ghi nhận mỗi khi khách thanh toán — suốt 1 năm
                      </p>
                    </div>
                  </div>
                </div>

                {/* Income Example */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                  <p className="text-blue-100 text-xs font-medium uppercase tracking-wider mb-2">
                    💰 Ví dụ thu nhập
                  </p>
                  <p className="text-white text-sm leading-relaxed">
                    Giới thiệu <strong>10 người</strong> mua gói 299K/tháng
                  </p>
                  <p className="text-2xl font-extrabold text-yellow-300 mt-1">
                    → 10.764.000đ/năm
                  </p>
                </div>

                <div className="mt-auto">
                  <button
                    onClick={handleStartAffiliate}
                    className="w-full py-4 bg-white text-indigo-700 rounded-xl font-bold hover:bg-opacity-95 transition duration-300 shadow-lg text-lg"
                  >
                    Bắt Đầu Kiếm 30% Hoa Hồng →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const handleContactClick = (type) => {
    trackContactClick(type);
  };

  const contactInfo = [
    {
      type: "phone",
      title: "Điện thoại",
      value: "0394.932.696",
      action: "tel:0394932696",
      bgColor: "from-blue-400 to-blue-600",
      icon: (
        <svg
          className="w-7 h-7 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      ),
    },
    {
      type: "facebook",
      title: "Facebook",
      value: "AutoWork Fanpage",
      action: "https://www.facebook.com/people/AutoWork/61564488969068/",
      bgColor: "from-indigo-400 to-indigo-600",
      icon: (
        <svg
          className="w-7 h-7 text-white"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M22.675 0h-21.35C.6 0 0 .6 0 1.337v21.326C0 23.4.6 24 1.337 24H12.82v-9.294H9.692v-3.622h3.128V8.41c0-3.1 1.894-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.762v2.31h3.59l-.467 3.622h-3.123V24h6.116C23.4 24 24 23.4 24 22.663V1.337C24 .6 23.4 0 22.675 0z" />
        </svg>
      ),
    },
    {
      type: "youtube",
      title: "YouTube",
      value: "Hướng dẫn sử dụng",
      action: "https://www.youtube.com/@autowork2025",
      bgColor: "from-red-500 to-red-600",
      icon: (
        <svg
          className="w-7 h-7 text-white"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
    },
    {
      type: "zalo",
      title: "Zalo",
      value: "zalo.me/autowork",
      action: "https://zalo.me/0394932696",
      bgColor: "from-cyan-400 to-cyan-600",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-7 h-7 text-white"
        >
          <path
            d="M19 6H5C4.5 6 4 6.5 4 7V8.5C4 9 4.5 9.5 5 9.5H15L5 17.5H5C4.5 17.5 4 18 4 18.5V20C4 20.5 4.5 21 5 21H19C19.5 21 20 20.5 20 20V18.5C20 18 19.5 17.5 19 17.5H9L19 9.5H19C19.5 9.5 20 9 20 8.5V7C20 6.5 19.5 6 19 6Z"
            fill="currentColor"
            strokeWidth="0"
          />
        </svg>
      ),
    },
  ];

  const [ref, isVisible] = useScrollAnimationOnce();

  return (
    <section
      id="contact"
      ref={ref}
      className={`py-12 md:py-24 bg-gradient-to-b from-gray-50 to-white transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="container mx-auto px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3 md:mb-4">
            Liên Hệ Với Chúng Tôi
          </h2>
          <p className="text-gray-600 text-center mb-8 md:mb-12">
            Hãy liên hệ với chúng tôi để được tư vấn chi tiết
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {contactInfo.map((info, index) => (
              <a
                key={info.type}
                href={info.action}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleContactClick(info.type)}
                className={`group relative overflow-hidden rounded-2xl bg-white p-6 md:p-8 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                }`}
                style={{
                  transitionDelay: `${index * 200}ms`,
                }}
              >
                {/* Gradient Background */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${info.bgColor} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}
                ></div>

                {/* Content */}
                <div className="relative flex flex-col items-center text-center space-y-4">
                  <div
                    className={`w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-r ${info.bgColor} shadow-lg group-hover:scale-110 transition-transform duration-300`}
                  >
                    {info.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-1">
                      {info.title}
                    </h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Component nút Trải Nghiệm Ngay nổi ở góc phải bên dưới
const FloatingButton = () => {
  const handleTryNow = React.useCallback(() => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "floating_button" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_floating", { location: "floating_button" });

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/auto-post";
    } else {
      sendToTelegram("Floating Button");
      window.location.href = "/login";
    }
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div className="relative group">
        {/* Hiệu ứng tỏa sáng ở viền */}
        <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 rounded-full blur-lg opacity-70 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>

        <button
          onClick={handleTryNow}
          className="relative px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center"
          aria-label="Trải nghiệm miễn phí ngay"
        >
          Trải Nghiệm Ngay
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 ml-2"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs md:text-sm px-2 py-1 rounded-full font-bold animate-pulse">
            Miễn phí
          </span>
        </button>
      </div>
    </div>
  );
};

// Component giới thiệu Cộng đồng Zalo AutoWork AI cao cấp (Glassmorphic)
const CommunitySection = () => {
  return (
    <section id="community" className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden relative">
      <div className="container mx-auto px-6 md:px-10 lg:px-20 xl:px-40">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#14389F] via-[#102C7D] to-[#0A1D54] text-white p-8 md:p-16 shadow-2xl border border-white/10">
          {/* Decor background shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full filter blur-3xl -ml-20 -mb-20"></div>
          
          <div className="relative z-10 grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3 space-y-6">
              <span className="inline-block px-4 py-1.5 bg-blue-500/30 text-cyan-200 border border-blue-400/20 rounded-full text-xs md:text-sm font-semibold tracking-wider uppercase">
                Kênh Nhận Bài Viết & Tài Liệu Thực Chiến
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold leading-tight">
                Kênh Chia Sẻ Ứng Dụng AI <br className="hidden md:inline" />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-teal-200">
                  Vào Công Việc Hằng Ngày
                </span>
              </h2>
              <p className="text-blue-100 text-base md:text-lg leading-relaxed">
                Kênh truyền thông chính thức dành cho những ai muốn làm chủ công nghệ và tự động hóa. Nơi bạn sẽ liên tục nhận được những bài viết chia sẻ thực chiến, những kiến thức và công cụ AI cực hay, miễn phí được chọn lọc hằng ngày từ đội ngũ Admin để giúp bạn tối ưu hóa hiệu suất làm việc nhờ AI.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:gap-6 text-sm text-blue-200 pt-2">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Kiến thức & Công cụ AI hay, miễn phí</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Xem bài chia sẻ hữu ích từ Admin</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Không tin rác, không trôi bài</span>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-2 flex flex-col items-center justify-center">
              {/* Zalo Premium Card */}
              <div className="w-full max-w-sm rounded-2xl p-6 bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl space-y-6 text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-to-tr from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg transform rotate-6 hover:rotate-0 transition-transform duration-500">
                  <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.89 13.93H9.24v-1.12l3.86-4.08H9.49V9.43h5.45v1.12l-3.86 4.08h4.81v1.3z" />
                  </svg>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-bold text-xl text-white">AutoWork AI Channel</h3>
                  <p className="text-sm text-blue-200">
                    Theo dõi các bài viết chia sẻ giải pháp tự động hóa nội dung & marketing đa kênh bằng AI từ Admin
                  </p>
                </div>
                
                <a
                  href="https://zalo.me/g/nwhx7ivthokpx20v1o5w"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-4 px-6 bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white font-bold rounded-xl shadow-lg hover:shadow-cyan-500/20 transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Tham Gia Kênh Zalo
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Component nút nổi thông minh Zalo ở góc trái bên dưới
const FloatingZaloWidget = () => {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 150) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 transition-all duration-500 ease-in-out transform translate-y-0 opacity-100">
      <div className="relative group">
        {/* Neon Zalo Pulse effect */}
        <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full blur-md opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
        <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-25"></div>
        
        <a
          href="https://zalo.me/g/nwhx7ivthokpx20v1o5w"
          target="_blank"
          rel="noopener noreferrer"
          className="relative w-14 h-14 bg-gradient-to-tr from-blue-500 via-blue-600 to-cyan-500 rounded-full shadow-2xl hover:shadow-cyan-400/50 flex items-center justify-center transform hover:-translate-y-1 hover:rotate-6 transition-all duration-300 border border-white/20"
          aria-label="Tham gia cộng đồng Zalo"
        >
          <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.89 13.93H9.24v-1.12l3.86-4.08H9.49V9.43h5.45v1.12l-3.86 4.08h4.81v1.3z" />
          </svg>
          
          {/* Tooltip */}
          <span className="absolute left-16 bg-gray-900/95 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-xl border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none font-medium">
            Cộng đồng Zalo AI 🚀
          </span>
        </a>
      </div>
    </div>
  );
};

const Footer = () => {
  // Đường dẫn logo và fallback
  const logoPath = "./images/logo1.png";
  const publicLogoPath = "/logo1.png";
  const fallbackLogo =
    "https://res.cloudinary.com/dtqlhleqr/image/upload/v1753212323/autowork-logo_mmsrzb.png";

  // Hàm xử lý lỗi tải logo
  const handleLogoError = (e) => {
    console.error("Lỗi tải logo trong footer:", e);
    if (e.target.src.includes(logoPath)) {
      e.target.src = publicLogoPath;
    } else if (e.target.src.includes(publicLogoPath)) {
      e.target.src = fallbackLogo;
    }
    e.target.onerror = null;
  };

  return (
    <footer className="bg-[#14389F] text-gray-300 py-8 md:py-12 relative">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDE2ek0zNiAzMHYySDI0di0yaDF6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-10"></div>
      <div className="container mx-auto px-6 relative">
        <div className="max-w-3xl mx-auto text-center space-y-4 md:space-y-6">
          <div className="h-12 mx-auto flex items-center justify-center">
            <img
              src={require("./images/logo1.png")}
              alt="AutoWork - Giải pháp AI tự động hóa Facebook WordPress và Zalo OA"
              className="h-full w-auto object-contain"
              loading="lazy"
              onError={handleLogoError}
            />
          </div>
          <p className="text-white">
            AutoWork - Giải pháp AI tự động viết và đăng bài cho Facebook
            Fanpage, WordPress/WooCommerce và Zalo OA. Tự động hóa content
            marketing đa kênh, tăng tương tác, traffic và doanh thu cho thương
            hiệu của bạn.
          </p>

          <div className="border-t border-gray-800 pt-6 text-sm text-white">
            &copy; 2024 AutoWork - Giải Pháp AI Tự Động Hóa Content Đa Kênh.
            All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

const SEO = () => {
  const currentDate = new Date().toISOString().split("T")[0];
  const logoUrl =
    "https://res.cloudinary.com/dtqlhleqr/image/upload/v1753212323/autowork-logo_mmsrzb.png";
  const siteUrl = "https://autowork.com.vn";
  const seoTitle =
    "AutoWork - Phần Mềm Tự Động Đăng Bài Đa Kênh Bằng AI";
  const seoDescription =
    "AutoWork giúp AI viết content, lên lịch đăng bài Facebook Fanpage, tạo bài chuẩn SEO WordPress/WooCommerce và đăng Zalo OA tự động.";
  const ogTitle =
    "AutoWork - AI Viết & Đăng Bài Facebook, WordPress, Zalo OA";
  const ogDescription =
    "Tự động tạo content bán hàng, bài SEO website, bài Zalo OA và trả lời bình luận Fanpage bằng AI trong một nền tảng.";
  const featureList = [
    "AI tự động viết bài Facebook Fanpage",
    "Phần mềm tự động đăng bài Facebook",
    "Tự động trả lời bình luận Facebook",
    "AI viết bài chuẩn SEO WordPress",
    "Tự động đăng bài WordPress",
    "Tự động đăng bài sản phẩm WooCommerce",
    "Tự động đăng bài Zalo OA",
    "Quản lý nội dung đa kênh",
  ];

  return (
    <Helmet>
      <html lang="vi" />
      <title>{seoTitle}</title>
      <meta charSet="utf-8" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5"
      />
      <meta
        name="description"
        content={seoDescription}
      />
      <meta
        name="keywords"
        content="phần mềm tự động đăng bài đa kênh bằng AI, AI tự động viết và đăng bài, phần mềm tự động đăng bài Facebook, quản lý fanpage bằng AI, tự động trả lời bình luận Facebook, auto comment Facebook, ẩn comment chống cướp khách, AI viết bài chuẩn SEO WordPress, tự động đăng bài WordPress, tối ưu SEO WordPress, tự động đăng bài sản phẩm WooCommerce, AI viết mô tả sản phẩm WooCommerce, tự động đăng bài Zalo OA, AI viết bài Zalo OA, quản lý nội dung Zalo OA, marketing automation AI, quản lý nội dung đa kênh"
      />
      <meta name="author" content="AutoWork" />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta name="last-modified" content={currentDate} />
      <meta name="theme-color" content="#2563EB" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="revisit-after" content="1 days" />
      <meta name="copyright" content="AutoWork" />
      <meta name="geo.region" content="VN" />
      <meta name="geo.position" content="10.762622;106.660172" />
      <meta name="ICBM" content="10.762622, 106.660172" />

      {/* Preconnect và Preload */}
      <link
        rel="preconnect"
        href="https://res.cloudinary.com"
        crossOrigin="anonymous"
      />
      <link rel="preload" as="image" href={logoUrl} />
      <link rel="preload" as="image" href="/images/dashboard.webp" />
      <link rel="canonical" href={siteUrl} />
      <link rel="alternate" href={siteUrl} hreflang="vi-VN" />
      <link rel="alternate" href={siteUrl} hreflang="x-default" />

      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="https://res.cloudinary.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://www.facebook.com" />

      {/* Resource Hints */}
      <link
        rel="preconnect"
        href="https://www.google-analytics.com"
        crossOrigin="anonymous"
      />
      <link
        rel="preconnect"
        href="https://www.facebook.com"
        crossOrigin="anonymous"
      />

      {/* Cache Control */}
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000" />
      <meta
        httpEquiv="Expires"
        content={new Date(Date.now() + 31536000000).toUTCString()}
      />

      {/* Link to Favicon */}
      <link rel="icon" href={logoUrl} type="image/png" />
      <link rel="apple-touch-icon" href={logoUrl} />

      {/* Structured Data - WebSite with SearchAction */}
      <script type="application/ld+json">
        {`
	          {
	            "@context": "https://schema.org",
	            "@type": "WebSite",
	            "name": "AutoWork",
	            "alternateName": "AutoWork - Phần mềm tự động đăng bài đa kênh bằng AI",
	            "url": "${siteUrl}",
	            "description": "${seoDescription}",
	            "inLanguage": "vi-VN",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "${siteUrl}/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        `}
      </script>

      {/* Structured Data - BreadcrumbList */}
      <script type="application/ld+json">
        {`
          {
	            "@context": "https://schema.org",
	            "@type": "BreadcrumbList",
	            "itemListElement": [{
	              "@type": "ListItem",
	              "position": 1,
	              "name": "Trang chủ",
	              "item": "${siteUrl}"
	            }]
	          }
        `}
      </script>

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "AutoWork",
            "url": "${siteUrl}",
            "logo": "${siteUrl}/logo1.png",
	            "description": "${seoDescription}",
            "sameAs": [
              "https://www.facebook.com/people/AutoWork/61564488969068/",
              "https://www.youtube.com/@autowork2025",
              "https://zalo.me/0394932696"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+84-394-932-696",
              "contactType": "customer service",
              "areaServed": "VN",
              "availableLanguage": "Vietnamese"
            },
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "VN",
              "addressLocality": "Ho Chi Minh City",
              "addressRegion": "Ho Chi Minh City"
            }
          }
        `}
      </script>

      {/* Structured Data - Software Application */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "AutoWork",
	            "description": "${seoDescription}",
            "url": "${siteUrl}",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "VND",
              "availability": "https://schema.org/InStock",
              "description": "Dùng thử miễn phí"
            },
	            "featureList": ${JSON.stringify(featureList)}
	          }
        `}
      </script>

      {/* Structured Data - HowTo */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
	            "name": "Cách tự động viết và đăng bài đa kênh với AutoWork",
	            "description": "Hướng dẫn từng bước sử dụng AutoWork để tạo nội dung bằng AI và đăng bài lên Facebook Fanpage, WordPress/WooCommerce hoặc Zalo OA.",
            "totalTime": "PT5M",
            "step": [{
              "@type": "HowToStep",
              "position": 1,
	              "name": "Đăng nhập và kết nối kênh",
	              "text": "Truy cập autowork.com.vn, đăng nhập và kết nối Facebook Fanpage, website WordPress hoặc Zalo OA bạn muốn quản lý."
	            }, {
              "@type": "HowToStep",
              "position": 2,
	              "name": "Chọn nền tảng và thiết lập chủ đề",
	              "text": "Chọn kênh cần đăng bài, nhập chủ đề, sản phẩm hoặc yêu cầu SEO. AI sẽ tự động tạo nội dung phù hợp với từng nền tảng."
            }, {
              "@type": "HowToStep",
              "position": 3,
              "name": "AI tạo nội dung tự động",
	              "text": "AutoWork AI sẽ viết content bán hàng, bài SEO website hoặc bài Zalo OA với cấu trúc phù hợp. Bạn có thể xem lại và chỉnh sửa trước khi đăng."
            }, {
              "@type": "HowToStep",
              "position": 4,
              "name": "Lên lịch và đăng bài tự động",
	              "text": "Chọn thời gian đăng hoặc bật chế độ tự động. Hệ thống sẽ đăng bài theo lịch trên kênh đã kết nối."
            }]
          }
        `}
      </script>

      {/* Structured Data - FAQ */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [{
              "@type": "Question",
              "name": "AutoWork có tự động đăng bài Facebook không?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Có. AutoWork hỗ trợ AI viết content, lên lịch đăng bài Facebook Fanpage và quản lý nhiều fanpage trong một giao diện."
              }
            }, {
              "@type": "Question",
              "name": "AutoWork có tự động trả lời bình luận Facebook không?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Có. AutoWork hỗ trợ tự động trả lời bình luận Facebook, nhận diện lead nóng và ẩn comment nhạy cảm để giảm thất thoát khách hàng."
              }
            }, {
              "@type": "Question",
              "name": "AutoWork có viết bài chuẩn SEO WordPress không?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Có. AutoWork tạo bài viết chuẩn SEO WordPress với tiêu đề, meta description, focus keyword, tags và nội dung có cấu trúc rõ ràng."
              }
            }, {
              "@type": "Question",
              "name": "AutoWork có hỗ trợ WooCommerce không?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Có. AutoWork có thể dùng dữ liệu sản phẩm WooCommerce để viết mô tả, chèn link sản phẩm và tạo nội dung bán hàng cho website hoặc kênh social."
              }
            }, {
              "@type": "Question",
              "name": "AutoWork có đăng bài Zalo OA không?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Có. AutoWork hỗ trợ AI viết bài Zalo OA dạng Article, tối ưu đọc trên mobile và đăng trực tiếp lên tài khoản OA đã kết nối."
              }
            }, {
              "@type": "Question",
              "name": "Nội dung AI của AutoWork có tối ưu SEO không?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Với WordPress, AutoWork tạo nội dung theo keyword, cấu trúc heading, meta description, tags và link nội bộ để hỗ trợ SEO on-page."
              }
            }, {
              "@type": "Question",
              "name": "AutoWork phù hợp với ai?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AutoWork phù hợp với shop online, SME, đội marketing và chủ website muốn quản lý nội dung đa kênh cho Facebook, WordPress/WooCommerce và Zalo OA."
              }
            }]
          }
        `}
      </script>

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={siteUrl} />
      <meta
        property="og:title"
        content={ogTitle}
      />
      <meta
        property="og:description"
        content={ogDescription}
      />
      <meta
        property="og:image"
        content="https://res.cloudinary.com/dtqlhleqr/image/upload/v1739841345/AutoWork/logo2025_copy_bi7z3l.jpg"
      />
      <meta property="og:locale" content="vi_VN" />
      <meta property="og:site_name" content="AutoWork" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={siteUrl} />
      <meta
        name="twitter:title"
        content={ogTitle}
      />
      <meta
        name="twitter:description"
        content={ogDescription}
      />
      <meta
        name="twitter:image"
        content="https://res.cloudinary.com/dtqlhleqr/image/upload/v1739841345/AutoWork/logo2025_copy_bi7z3l.jpg"
      />
    </Helmet>
  );
};

const LandingPage1 = () => {
  // [TẮT TẾT 2026] Hook quản lý hiệu ứng hoa mai, hoa đào Tết - đã hết lễ
  // const { snowEnabled, toggleSnow } = useSnowEffect();

  React.useEffect(() => {
    // Xử lý affiliate link khi component mount
    handleAffiliateLink();

    // Capture first-touch source attribution (chỉ lưu lần đầu)
    captureFirstTouch();

    // Bắt và lưu tham số quảng cáo Facebook (backward-compatible)
    captureFacebookAdParams();

    // Tối ưu scroll behavior
    if ("scrollBehavior" in document.documentElement.style) {
      document.documentElement.style.scrollBehavior = "smooth";
    }

    // Xử lý hash scroll với debounce
    const handleHashScroll = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          const headerHeight = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.pageYOffset - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }
    };

    // Debounce function
    const debounce = (func, wait) => {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    };

    const debouncedHandleHashScroll = debounce(handleHashScroll, 100);
    debouncedHandleHashScroll();

    // Cleanup
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  // Memoize các components để tránh re-render không cần thiết
  const MemoizedHeader = React.memo(Header);
  const MemoizedHeroSection = React.memo(HeroSection);
  const MemoizedPainPointsSection = React.memo(PainPointsSection);
  const MemoizedAboutSection = React.memo(AboutSection);
  const MemoizedLeadConversionEngineSection = React.memo(LeadConversionEngineSection);
  const MemoizedFeaturesSection = React.memo(FeaturesSection);
  const MemoizedComparisonSection = React.memo(ComparisonSection);
  const MemoizedPartnerToolsSection = React.memo(PartnerToolsOverviewSection);
  // const MemoizedAIAppServiceSection = React.memo(AIAppServiceSection);

  const MemoizedTestimonialsSection = React.memo(TestimonialsSection);
  const MemoizedCommunitySection = React.memo(CommunitySection);
  const MemoizedAffiliateSection = React.memo(AffiliateSection);
  const MemoizedContactSection = React.memo(ContactSection);
  const MemoizedFooter = React.memo(Footer);

  return (
    <div className="bg-gray-100">
      {/* [TẮT TẾT 2026] Hiệu ứng hoa mai, hoa đào Tết - đã hết lễ */}
      {/* <Snowfall isEnabled={snowEnabled} /> */}
      {/* <SnowToggleButton isEnabled={snowEnabled} onToggle={toggleSnow} /> */}
      
      <SEO />
      <MemoizedHeader />
      <AffiliateBanner />
      <main>
        <article>
          <MemoizedHeroSection />
          <MemoizedPainPointsSection />
          <MemoizedAboutSection />
          <MemoizedLeadConversionEngineSection />
          <MemoizedFeaturesSection />
          <MemoizedComparisonSection />
          <MemoizedPartnerToolsSection />

          {/* <MemoizedAIAppServiceSection /> */}
          <MemoizedCommunitySection />
          <MemoizedTestimonialsSection />
          <MemoizedAffiliateSection />
          <MemoizedContactSection />
        </article>
      </main>
      <MemoizedFooter />
      <FloatingZaloWidget />
      <FloatingButton />
    </div>
  );
};

export { Header };
export default LandingPage1;
